import React, { Component, PropTypes } from 'react';
import { get, isString, uniq, debounce } from 'lodash';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { PopoverEx as SparkPopover, ControlledSelect } from '../../ui/sabre-spark-react-ex/index';
import { onThemePropChange } from '../../helpers/ThemeStoreHelpers';
import { CALCULATE_FONT_USAGE } from '../../../constants/ActionTypes';
import './FontConfigurator.scss';
import '../../../styles/styles.scss';
import FontConfiguratorRow from './FontConfiguratorRow';
import { FontFamilyProperty } from '../../../../lib/css-property-parser/FontFamilyProperty';
import { checkPermissionForLevel } from '../../../../lib/utils';
import { ACCESS_POINTS_GROUP, ACCESS_POINT_LEVEL } from '../../../constants/AccessPoints';
import InputTextField from 'components/common/InputTextField';

const fontWeights = [100, 200, 300, 400, 500, 600, 700, 800, 900].map(v => ({ key: v, label: v }));
const fontStyles = ['normal', 'italic', 'oblique'].map(v => ({ key: v, label: v }));

class FontConfigurator extends Component {
    static propTypes = {
        fontStyleProperty: PropTypes.string,
        fontFamilyProperty: PropTypes.string.isRequired,
        fontWeightProperty: PropTypes.string,
        prependHook: PropTypes.node,
        fontFamilyLabel: PropTypes.string,
        fontFamilyClass: PropTypes.string,
        fontStyleClass: PropTypes.string,
        fontWeightClass: PropTypes.string,
        className: PropTypes.string
    };
    static defaultProps = {
        fontStyleProperty: '',
        fontFamilyProperty: '',
        fontWeightProperty: '',
        prependHook: null,
        fontFamilyLabel: 'Font Family',
        fontFamilyClass: 'col-xs-12 col-sm-3',
        fontWeightClass: 'col-xs-12 col-sm-3',
        fontStyleClass: 'col-xs-12 col-sm-3',
        className: ''
    };
    constructor(props) {
        super(props);
        this.state = {
            fontFamilies: new FontFamilyProperty(props.font),
            manualFontInput: props.font,
            fontSearch: '',
            addOtherFont: '',
            nestedPopovers: []
        };
        this.state.maxFonts = this.state.fontFamilies.value.length;
        this._handleChange = debounce(this._handleChange.bind(this), 200);
    }
    componentWillReceiveProps(nextProps) {
        if (this.props.font !== nextProps.font) {
            const nextFontFamilies = new FontFamilyProperty(nextProps.font);
            this.setState({
                fontFamilies: nextFontFamilies,
                manualFontInput: nextProps.font,
                maxFonts: Math.max(nextFontFamilies.value.length, 2)
            });
        }
    }
    _handleChange(newValue) {
        this.props.onFontPropertyChange(this.props.fontFamilyProperty)(newValue);
        if (this.popoverRef) {
            this.popoverRef._sparkInstance.update();
        }
    }
    onManualChange = value => {
        this.setState(
            {
                manualFontInput: value
            },
            () => {
                this._handleChange(value);
            }
        );
    };
    onChange(index, value) {
        this.setState(
            prevState => {
                prevState.fontFamilies.value.splice(index, 1, value);
                return { fontFamilies: prevState.fontFamilies };
            },
            () => this._handleChange(this.state.fontFamilies.build())
        );
    }
    nestedPopoverHandler = (key, node) => {
        this.state.nestedPopovers[key] = node;
    };
    renderRows(fontFamilies) {
        const result = [];
        for (let i = 0; i < this.state.maxFonts; i++) {
            result.push(
                <FontConfiguratorRow
                    key={i}
                    value={fontFamilies[i] ? fontFamilies[i] : null}
                    title={i > 0 ? `Fallback font ${i}` : 'Default'}
                    nestedPopoverHandler={node => this.nestedPopoverHandler(i, node)}
                    onChange={value => this.onChange(i, value)}
                />
            );
        }
        return result;
    }
    render() {
        const choosenFontFamilies = this.state.fontFamilies.value;
        const errorMessage = !FontFamilyProperty.isValid(this.state.manualFontInput) && 'Invalid font name.  Use single quotation marks for each font-family with more than one word.';
        return (
            <div className={classNames('row', 'font-configurator', this.props.className)}>
                {this.props.prependHook}
                <div className={this.props.fontFamilyClass}>
                    <SparkPopover
                        ref={node => (this.popoverRef = node)}
                        isDisabled={!this.props.permissionEdit}
                        anchorX="right"
                        className="col-xs-4"
                        nestedPopovers={this.state.nestedPopovers}
                        toggleEl={
                            <label
                                className={
                                    !this.props.permissionEdit
                                        ? 'spark-select has-value disabled'
                                        : 'spark-select has-value'
                                }
                            >
                                <div className={'spark-select__input font-configurator__trigger'}>
                                    <div>{this.props.font}</div>
                                </div>
                                <span className="spark-label">{this.props.fontFamilyLabel}</span>
                            </label>
                        }
                    >
                        <div className={'row'}>
                            <div className={'col-xs-12'}>
                                <strong>Enter CSS or select your fonts</strong>
                            </div>
                        </div>
                        <div className={'row'}>
                            <div className={'col-xs-12'}>
                                <InputTextField
                                    value={this.state.manualFontInput}
                                    isDisabled={this.props.permissionEdit}
                                    onChange={this.onManualChange}
                                    sparkInputProps={{ errorMessage }}
                                />
                                <span className="input-hint">Enter fonts comma delimited, with single quotation marks for each font-family with more than one word., e.g., Helvetica, 'Helvetica Neue'</span>
                            </div>
                        </div>
                        {this.renderRows(choosenFontFamilies)}
                        <div className={'row spark-pad-t-1 spark-pad-b-1'}>
                            <div
                                style={{
                                    fontSize: '1.5rem',
                                    color: '#23826F'
                                }}
                                className={'col-xs-12 cursor-pointer'}
                                onClick={() => {
                                    this.setState((prevState, props) => {
                                        return { maxFonts: prevState.maxFonts + 1 };
                                    });
                                }}
                            >
                                + Add another fallback font
                            </div>
                        </div>
                    </SparkPopover>
                </div>
                {this.props.fontStyleProperty && (
                    <div className={this.props.fontStyleClass}>
                        <ControlledSelect
                            label="Font Style"
                            onChange={this.props.onFontPropertyChange(this.props.fontStyleProperty)}
                            isDisabled={!this.props.permissionEdit}
                            value={this.props.style}
                            options={fontStyles}
                        />
                    </div>
                )}
                {this.props.fontWeightProperty && (
                    <div className={this.props.fontWeightClass}>
                        <ControlledSelect
                            label="Font Weight"
                            onChange={this.props.onFontPropertyChange(this.props.fontWeightProperty)}
                            isDisabled={!this.props.permissionEdit}
                            value={this.props.weight}
                            options={fontWeights}
                        />
                    </div>
                )}
                {this.props.children}
            </div>
        );
    }
}

const mapStateToProps = (state, ownProps) => {
    return {
        availableFontFamilies: state.theme.fonts,
        currentTheme: state.app.data.theme,
        permissionEdit: checkPermissionForLevel(
            state.oauth.accessLevel,
            ACCESS_POINTS_GROUP.THEMES,
            ACCESS_POINT_LEVEL.EDIT,
            state.oauth.userPermissionLevels,
            state.app.selected.level
        ),
        font: get(state, `app.data.theme.${ownProps['fontFamilyProperty']}`),
        weight: get(state, `app.data.theme.${ownProps['fontWeightProperty']}`),
        style: get(state, `app.data.theme.${ownProps['fontStyleProperty']}`)
    };
};

const mapDispatchToProps = dispatch => {
    return {
        onFontPropertyChange: onThemePropChange(dispatch, null, dispatch =>
            dispatch({ type: CALCULATE_FONT_USAGE, changed: true })
        ),
        onThemePropChange: onThemePropChange(dispatch, null)
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(FontConfigurator);


// WEBPACK FOOTER //
// ./src/components/widgets/FontConfigurator/FontConfigurator.js